import { TableRow } from '@code/innovance-react-ui-kit';
import React, { createContext, FC, useContext, useReducer } from 'react';

import {
  customerSearchDefaultValues,
  SearchFormData,
} from 'src/front/components/CustomerSearchForm';

enum ACTIONS {
  SEARCH_CLIENT,
  CLEAR,
}

type Action =
  | {
      type: ACTIONS.SEARCH_CLIENT;
      payload: {
        customerSearchFormValues: SearchFormData;
        customerSearchValues: CustomerSearchValues;
      };
    }
  | { type: ACTIONS.CLEAR };

interface CustomerSearchValues {
  customerSurname: string;
  customerName: string;
  customerSecondName: string;
  birthDate: Date | null;
  customerId: string;
  customerNumber: string;
  clientName: string;
  selectedCustomer: TableRow;
  customerPageNumber: number;
}

interface ProblemTrackingSystemState {
  customerSearchFormValues: SearchFormData;
  customerSearchValues: CustomerSearchValues;
}

export const initialValues = {
  customerSearchFormValues: customerSearchDefaultValues,
  customerSearchValues: {
    customerSurname: '',
    customerName: '',
    customerSecondName: '',
    birthDate: null,
    customerId: '',
    customerNumber: '',
    clientName: '',
    selectedCustomer: {
      id: '',
      columns: [],
    },
    customerPageNumber: 1,
  },
};

type ProblemTrackingSystemDispatch = (action: Action) => void;

const ProblemTrackingSystemStateContext = createContext<ProblemTrackingSystemState | undefined>(
  undefined
);
const ProblemTrackingSystemDispatchContext = createContext<
  ProblemTrackingSystemDispatch | undefined
>(undefined);

const problemTrackingSystemReducer = (
  state: ProblemTrackingSystemState,
  action: Action
): ProblemTrackingSystemState => {
  switch (action.type) {
    case ACTIONS.SEARCH_CLIENT:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const ProblemTrackingSystemProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(problemTrackingSystemReducer, initialValues);
  return (
    <ProblemTrackingSystemStateContext.Provider value={state}>
      <ProblemTrackingSystemDispatchContext.Provider value={dispatch}>
        {children}
      </ProblemTrackingSystemDispatchContext.Provider>
    </ProblemTrackingSystemStateContext.Provider>
  );
};

export const useProblemTrackingSystemState = (): ProblemTrackingSystemState => {
  const context = useContext(ProblemTrackingSystemStateContext);
  if (context === undefined) {
    throw new Error(
      'useProblemTrackingSystemState must be used within a ProblemTrackingSystemProvider'
    );
  }

  return context;
};

type SearchInput = Pick<
  ProblemTrackingSystemState,
  'customerSearchFormValues' | 'customerSearchValues'
>;

export const useClientSearchDispatch = (): ((payload: SearchInput) => void) => {
  const dispatch = useContext(ProblemTrackingSystemDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClientSearchDispatch must be used within a ProblemTrackingSystemProvider');
  }

  return (payload: SearchInput) => dispatch({ type: ACTIONS.SEARCH_CLIENT, payload });
};

export const useClearDispatch = (): (() => void) => {
  const dispatch = useContext(ProblemTrackingSystemDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a ProblemTrackingSystemProvider');
  }

  return () => dispatch({ type: ACTIONS.CLEAR });
};

export default ProblemTrackingSystemProvider;
